import { Loader2 } from 'lucide-react';
import type { MutableRefObject, ReactNode } from 'react';

export interface GraphCanvasProps {
  readonly containerRef: MutableRefObject<HTMLDivElement | null>;
  readonly loading: boolean;
  readonly error: unknown;
  readonly children?: ReactNode;
}

export function GraphCanvas({
  containerRef,
  loading,
  error,
  children,
}: GraphCanvasProps): JSX.Element {
  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden bg-void">
      <div
        className="absolute inset-0 cursor-grab"
        ref={containerRef}
        style={{
          backgroundImage:
            'radial-gradient(circle at 25% 20%, rgba(124,58,237,0.10) 0, transparent 45%),' +
            ' radial-gradient(circle at 80% 80%, rgba(59,130,246,0.08) 0, transparent 50%),' +
            ' linear-gradient(180deg, rgba(6,6,10,0) 0%, rgba(6,6,10,0.6) 100%)',
        }}
      />
      {loading ? <Overlay text="Loading graph…" spinner /> : null}
      {error ? <Overlay text={`Failed to load graph: ${describe(error)}`} /> : null}
      {children}
    </div>
  );
}

function Overlay({
  text,
  spinner,
}: {
  readonly text: string;
  readonly spinner?: boolean;
}): JSX.Element {
  return (
    <div className="absolute inset-0 z-[6] grid place-items-center bg-void/70 backdrop-blur-sm">
      <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-surface/80 px-5 py-3 font-mono text-xs text-text-secondary shadow-panel">
        {spinner && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
        <span>{text}</span>
      </div>
    </div>
  );
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
